import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

const FESTIVALS_COLLECTION = 'festivals';
const LOCAL_STORAGE_KEY = 'hrm_festivals_cache';

// Helper to get cached festivals
const getCachedFestivals = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.warn('Could not read cached festivals:', e);
    return [];
  }
};

// Helper to save cached festivals
const setCachedFestivals = (festivals) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(festivals));
  } catch (e) {
    console.warn('Could not cache festivals:', e);
  }
};

/**
 * Fetch all festivals (ordered by date)
 */
export const getFestivals = async () => {
  try {
    const q = query(collection(db, FESTIVALS_COLLECTION), orderBy('date', 'asc'));
    const snapshot = await getDocs(q);
    const list = [];
    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      list.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
      });
    });

    if (list.length > 0) {
      setCachedFestivals(list);
      return list;
    }

    // If Firestore collection is empty, return any cached festivals
    return getCachedFestivals();
  } catch (error) {
    console.warn('[FestivalService] Error fetching festivals from Firestore, using cache:', error);
    return getCachedFestivals();
  }
};

/**
 * Add a new festival
 * @param {Object} festival - { date: 'YYYY-MM-DD', name: 'Diwali', description: '', createdBy: '' }
 */
export const addFestival = async ({ date, name, description = '', createdBy = '' }) => {
  if (!date || !name) throw new Error('Date and Festival Name are required');

  const newFestivalData = {
    date: String(date).trim(),
    name: String(name).trim(),
    description: String(description || '').trim(),
    createdBy: createdBy || 'admin',
    createdAt: serverTimestamp()
  };

  try {
    const docRef = await addDoc(collection(db, FESTIVALS_COLLECTION), newFestivalData);
    const saved = {
      id: docRef.id,
      ...newFestivalData,
      createdAt: new Date().toISOString()
    };

    // Update local cache
    const current = getCachedFestivals().filter(f => f.date !== date);
    current.push(saved);
    current.sort((a, b) => a.date.localeCompare(b.date));
    setCachedFestivals(current);

    return saved;
  } catch (error) {
    console.warn('[FestivalService] Failed to write to Firestore, storing locally:', error);
    // Fallback: save to local cache with pseudo-id
    const localId = 'local_' + Date.now();
    const saved = {
      id: localId,
      ...newFestivalData,
      createdAt: new Date().toISOString()
    };
    const current = getCachedFestivals().filter(f => f.date !== date);
    current.push(saved);
    current.sort((a, b) => a.date.localeCompare(b.date));
    setCachedFestivals(current);
    return saved;
  }
};

/**
 * Update an existing festival
 * @param {string} festivalId
 * @param {Object} data - { date, name, description, updatedBy }
 */
export const updateFestival = async (festivalId, { date, name, description = '', updatedBy = '' }) => {
  if (!date || !name) throw new Error('Date and Festival Name are required');

  const updateData = {
    date: String(date).trim(),
    name: String(name).trim(),
    description: String(description || '').trim(),
    updatedBy: updatedBy || 'admin',
    updatedAt: serverTimestamp()
  };

  try {
    if (festivalId && !festivalId.startsWith('local_')) {
      await updateDoc(doc(db, FESTIVALS_COLLECTION, festivalId), updateData);
    }
  } catch (error) {
    console.warn('[FestivalService] Error updating festival in Firestore:', error);
  } finally {
    // Update local cache
    const current = getCachedFestivals().map(f => {
      if (f.id === festivalId || (date && f.date === date)) {
        return {
          ...f,
          ...updateData,
          updatedAt: new Date().toISOString()
        };
      }
      return f;
    });
    setCachedFestivals(current);
  }
  return true;
};

/**
 * Delete a festival
 * @param {string} festivalId
 * @param {string} date
 */
export const deleteFestival = async (festivalId, date = null) => {
  try {
    if (festivalId && !festivalId.startsWith('local_')) {
      await deleteDoc(doc(db, FESTIVALS_COLLECTION, festivalId));
    }
  } catch (error) {
    console.warn('[FestivalService] Error deleting festival from Firestore:', error);
  } finally {
    // Always remove from local cache
    const current = getCachedFestivals().filter(f => f.id !== festivalId && (!date || f.date !== date));
    setCachedFestivals(current);
  }
  return true;
};
